import { Component, OnInit, OnDestroy } from '@angular/core';
import { NotificationsService } from '../../../services/notifications.service';
import { TosterService } from '../../../services/toster.service';
import { Subscription } from 'rxjs';
import {
  DEFAULT_NOTIFICATION_CONFIG,
  NOTIFICATION_SOUNDS,
  NotificationConfig
} from '../../config/notification.config';

export interface NotificationPayload {
  notification?: {
    title: string;
    body: string;
    icon?: string;
  };
  data?: {
    title?: string;
    body?: string;
    sound?: string;
    messageId?: string;
    [key: string]: any;
  };
}

@Component({
  selector: 'app-notification-display',
  standalone: true,
  template: ``,
  styles: []
})
export class NotificationDisplayComponent implements OnInit, OnDestroy {
  private notificationSubscription?: Subscription;
  private config: NotificationConfig = DEFAULT_NOTIFICATION_CONFIG;

  constructor(
    private notificationService: NotificationsService,
    private toasterService: TosterService
  ) {}

  ngOnInit() {
    // Subscribe to foreground messages
    this.notificationSubscription = this.notificationService.messagePayload$.subscribe(
      (payload: NotificationPayload | null) => {
        if (payload) {
          this.showNotification(payload);
        }
      }
    );
  }

  ngOnDestroy() {
    if (this.notificationSubscription) {
      this.notificationSubscription.unsubscribe();
    }
  }

  private showNotification(payload: NotificationPayload) {
    console.log('Displaying foreground notification:', payload);

    // Play notification sound if specified
    this.playNotificationSound(payload);

    // Show toaster notification
    if (this.config.showToaster) {
      const title = this.getNotificationTitle(payload);
      const body = this.getNotificationBody(payload);
      this.toasterService.showInfo(`${title}: ${body}`);
    }
  }

  private playNotificationSound(payload: NotificationPayload) {
    if (!this.config.playSound) return;

    const soundType = payload.data?.sound || 'default';
    const soundFile = NOTIFICATION_SOUNDS[soundType] || NOTIFICATION_SOUNDS['default'];

    if (soundFile && soundType !== 'default') {
      try {
        const audio = new Audio(`/assets/sounds/${soundFile}`);
        audio.volume = 0.7;
        audio.play().catch(e => {
          console.log('Could not play custom notification sound:', e);
          this.playDefaultNotificationSound();
        });
      } catch (e) {
        console.log('Error playing custom sound:', e);
        this.playDefaultNotificationSound();
      }
    } else {
      // Play default notification sound
      this.playDefaultNotificationSound();
    }
  }

  private playDefaultNotificationSound() {
    try {
      // Create a simple notification sound using Web Audio API
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);

      oscillator.frequency.value = 800;
      oscillator.type = 'sine';

      gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);

      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.5);
    } catch (e) {
      console.log('Could not play default notification sound:', e);
    }
  }

  private getNotificationTitle(payload: NotificationPayload): string {
    return payload?.notification?.title ||
           payload?.data?.title ||
           'New Notification';
  }

  private getNotificationBody(payload: NotificationPayload): string {
    return payload?.notification?.body ||
           payload?.data?.body ||
           'You have a new message.';
  }
}
